/**
 * DebugDashboardRenderer.ts
 *
 * Renders a comprehensive debug dashboard overlay showing:
 * - Input state (typed characters, caret range, lag)
 * - Cursor information (position, caret map presence)
 * - Coverage metrics (caret count, line count)
 * - Multi-paragraph information (ranges, per-paragraph stats)
 * - Last edit tracking (type, position, age, paragraph delta)
 * - Diagnostics and text preview
 *
 * Extracted from EdgeDocsCanvas.V3.tsx to improve maintainability.
 */

import { TypesetterWorkerClient } from '../../../../workers/TypesetterWorkerClient';

/**
 * Parameters required to render the debug dashboard
 */
export interface DebugDashboardParams {
  /** The overlay canvas element to render on */
  overlayCanvas: HTMLCanvasElement;
  /** The current WASM snapshot (optional) */
  currentSnapshot: any;
  /** The input handler with getCurrentState method */
  inputHandler: any;
  /** The typesetter client for accessing cached snapshots */
  typesetterClient: TypesetterWorkerClient | null;
  /** Shared input context for accessing last edit info */
  sharedInputContext?: {
    getLastEditInfo?: () => {
      type: 'insert' | 'delete' | 'unknown';
      position: number;
      deleteCount: number;
      insertLength: number;
      deletedParagraphIds: string[];
      prevParagraphCount: number;
      newParagraphCount: number;
      timestamp: number;
    } | null;
  } | null;
  /** Scale factor for coordinate conversion */
  scale: number;
  /** Current snapshot version number */
  snapshotVersion: number;
}

/**
 * Helper: Get grapheme count from text using Intl.Segmenter
 */
const getGraphemeCount = (text: string): number => {
  try {
    const segmenter = new (Intl as any).Segmenter('en', { granularity: 'grapheme' });
    return Array.from(segmenter.segment(text)).length;
  } catch {
    return text.length; // Fallback to character count
  }
};

/**
 * Helper: Draw rounded rectangle on canvas (polyfill for older browsers)
 */
const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

/**
 * Renders the debug dashboard overlay
 *
 * @param params - Configuration and data required for rendering
 */
export const drawDebugDashboard = (params: DebugDashboardParams): void => {
  const {
    overlayCanvas,
    currentSnapshot,
    inputHandler,
    typesetterClient,
    sharedInputContext,
    scale,
    snapshotVersion
  } = params;

  // 🔧 FIX: Only require overlay canvas, not snapshot - dashboard should still render
  // with placeholder values if snapshot is temporarily unavailable
  if (!overlayCanvas) return;
  const ctx = overlayCanvas.getContext('2d');
  if (!ctx) return;

  // ✅ Get BOTH perspectives for user experience
  const inputState = inputHandler.getCurrentState();
  const typedText = inputState?.text ?? ''; // What user typed
  const cursorPos = inputState?.selection?.focus ?? 0; // Current cursor position

  // 🔬 MULTI-PARAGRAPH FIX: Get ALL cached snapshots, not just currentSnapshot
  const cachedSnapshots = typesetterClient?.getCachedParagraphSnapshots() || [];
  const snapshotCount = cachedSnapshots.length;

  // Calculate TOTAL caret entries across all paragraphs
  let totalCaretEntries = 0;
  let totalLines = 0;
  let minCaretIdx = Infinity;
  let maxCaretIdx = -1;
  const perParaInfo: string[] = [];
  const paraRanges: string[] = []; // Per-paragraph grapheme ranges

  for (const snap of cachedSnapshots) {
    const carets = snap?.caret_map?.length || 0;
    const lines = snap?.lines?.length || 0;
    totalCaretEntries += carets;
    totalLines += lines;
    perParaInfo.push(`${carets}c/${lines}L`);

    // Track grapheme ranges per paragraph
    if (snap?.lines?.length > 0) {
      const firstLine = snap.lines[0];
      const lastLine = snap.lines[snap.lines.length - 1];
      const start = firstLine?.start_grapheme_index ?? 0;
      const end = lastLine?.end_grapheme_index ?? 0;
      paraRanges.push(`[${start}-${end})`);
    } else if (snap?.caret_map?.length > 0) {
      // Empty paragraph - use caret index
      const idx = snap.caret_map[0]?.grapheme_index ?? 0;
      paraRanges.push(`[${idx}]`);
    }

    // Track min/max caret indices for bounds checking
    for (const entry of (snap?.caret_map || [])) {
      if (entry.grapheme_index < minCaretIdx) minCaretIdx = entry.grapheme_index;
      if (entry.grapheme_index > maxCaretIdx) maxCaretIdx = entry.grapheme_index;
    }
  }

  // For single snapshot fallback (legacy behavior)
  const singleSnapshotCarets = currentSnapshot?.caret_map?.length || 0;

  // Check if caret indices are within document bounds
  const caretOutOfBounds = maxCaretIdx > typedText.length;

  // Check if cursor position can be found in caret map
  let cursorFound = false;
  for (const snap of cachedSnapshots) {
    if (snap?.caret_map?.some((c: any) => c.grapheme_index === cursorPos)) {
      cursorFound = true;
      break;
    }
  }

  const typedGraphemes = getGraphemeCount(typedText);
  const isStale = maxCaretIdx !== typedText.length;
  const expectedCarets = typedText.length + 1;
  const delta = typedText.length - maxCaretIdx;
  const caretCoverage = expectedCarets > 0 ? ((totalCaretEntries / expectedCarets) * 100).toFixed(0) : '0';

  // Build diagnostic messages
  const diagnostics: string[] = [];
  if (isStale) diagnostics.push('Stale');
  if (caretOutOfBounds) diagnostics.push('Caret>DocLen');
  if (!cursorFound) diagnostics.push('Cursor missing');
  if (delta < 0) diagnostics.push('Offset doubled?');

  // ═══════════════════════════════════════════════════════════════
  // DASHBOARD LAYOUT - Wider, two-column design
  // ═══════════════════════════════════════════════════════════════
  const W = 550; // Wider dashboard
  const H = 360; // Taller to accommodate LAST EDIT section
  const X = (overlayCanvas.width / scale - W) / 2;
  const Y = 380;
  const COL1 = X + 12;
  const COL2 = X + 280; // Second column start
  const LABEL_W = 110; // Label width

  ctx.save();

  // Main background with shadow effect
  ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
  ctx.fillRect(X + 4, Y + 4, W, H); // Shadow
  ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
  ctx.fillRect(X, Y, W, H);

  // Border color based on status
  ctx.strokeStyle = isStale ? 'rgba(220, 53, 69, 0.9)' : 'rgba(40, 167, 69, 0.9)';
  ctx.lineWidth = 3;
  ctx.strokeRect(X, Y, W, H);

  // ═══════════════════════════════════════════════════════════════
  // HEADER: Title + Status Badge
  // ═══════════════════════════════════════════════════════════════
  ctx.fillStyle = 'rgba(248, 249, 250, 1)';
  ctx.fillRect(X + 2, Y + 2, W - 4, 32);

  ctx.fillStyle = '#212529';
  ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';
  ctx.fillText('📊 Debug Dashboard v3', COL1, Y + 22);

  // Status badge
  const badgeX = X + W - 80;
  ctx.fillStyle = isStale ? 'rgba(220, 53, 69, 0.9)' : 'rgba(40, 167, 69, 0.9)';
  drawRoundedRect(ctx, badgeX, Y + 8, 65, 20, 4);
  ctx.fill();
  ctx.fillStyle = 'white';
  ctx.font = 'bold 11px system-ui';
  ctx.fillText(isStale ? 'STALE' : 'FRESH', badgeX + (isStale ? 14 : 12), Y + 22);

  let row = Y + 50; // Start after header
  const ROW_H = 16; // Row height

  // ═══════════════════════════════════════════════════════════════
  // LEFT COLUMN: Input & Cursor
  // ═══════════════════════════════════════════════════════════════
  ctx.font = '11px system-ui, -apple-system, sans-serif';

  // Section: Input
  ctx.fillStyle = '#6c757d';
  ctx.font = 'bold 10px system-ui';
  ctx.fillText('INPUT', COL1, row);
  row += ROW_H;

  ctx.font = '11px monospace';
  ctx.fillStyle = '#495057';
  ctx.fillText('Typed:', COL1, row);
  ctx.fillStyle = '#0d6efd';
  ctx.fillText(`${typedText.length} chars`, COL1 + LABEL_W, row);
  row += ROW_H;

  ctx.fillStyle = '#495057';
  ctx.fillText('Caret range:', COL1, row);
  ctx.fillStyle = caretOutOfBounds ? '#dc3545' : '#198754';
  const rangeText = `[${minCaretIdx === Infinity ? '-' : minCaretIdx}–${maxCaretIdx === -1 ? '-' : maxCaretIdx}]`;
  ctx.fillText(rangeText + (caretOutOfBounds ? ' ⚠️' : ''), COL1 + LABEL_W, row);
  row += ROW_H;

  ctx.fillStyle = '#495057';
  ctx.fillText('Lag:', COL1, row);
  ctx.fillStyle = delta === 0 ? '#198754' : (delta < 0 ? '#fd7e14' : '#dc3545');
  ctx.fillText(`${delta > 0 ? '+' : ''}${delta}${delta < 0 ? ' (AHEAD)' : ''}`, COL1 + LABEL_W, row);
  row += ROW_H + 8;

  // Section: Cursor
  ctx.fillStyle = '#6c757d';
  ctx.font = 'bold 10px system-ui';
  ctx.fillText('CURSOR', COL1, row);
  row += ROW_H;

  ctx.font = '11px monospace';
  ctx.fillStyle = '#495057';
  ctx.fillText('Position:', COL1, row);
  ctx.fillStyle = '#0d6efd';
  ctx.fillText(`${cursorPos}`, COL1 + LABEL_W, row);
  row += ROW_H;

  ctx.fillStyle = '#495057';
  ctx.fillText('In caret map:', COL1, row);
  ctx.fillStyle = cursorFound ? '#198754' : '#dc3545';
  ctx.fillText(cursorFound ? '✓ Found' : '✗ Missing', COL1 + LABEL_W, row);
  row += ROW_H + 8;

  // Section: Coverage
  ctx.fillStyle = '#6c757d';
  ctx.font = 'bold 10px system-ui';
  ctx.fillText('COVERAGE', COL1, row);
  row += ROW_H;

  ctx.font = '11px monospace';
  ctx.fillStyle = '#495057';
  ctx.fillText('Carets:', COL1, row);
  ctx.fillStyle = totalCaretEntries >= expectedCarets ? '#198754' : '#fd7e14';
  ctx.fillText(`${totalCaretEntries}/${expectedCarets} (${caretCoverage}%)`, COL1 + LABEL_W, row);
  row += ROW_H;

  ctx.fillStyle = '#495057';
  ctx.fillText('Lines:', COL1, row);
  ctx.fillStyle = totalLines > 0 ? '#198754' : '#dc3545';
  ctx.fillText(`${totalLines}`, COL1 + LABEL_W, row);

  // Save left column's final row position
  const leftColEndRow = row;

  // ═══════════════════════════════════════════════════════════════
  // RIGHT COLUMN: Paragraphs & Snapshot
  // ═══════════════════════════════════════════════════════════════
  row = Y + 50;

  // Section: Paragraphs
  ctx.fillStyle = '#6c757d';
  ctx.font = 'bold 10px system-ui';
  ctx.fillText('PARAGRAPHS', COL2, row);
  row += ROW_H;

  ctx.font = '11px monospace';
  ctx.fillStyle = '#495057';
  ctx.fillText('Count:', COL2, row);
  ctx.fillStyle = snapshotCount > 0 ? '#198754' : '#dc3545';
  ctx.fillText(`${snapshotCount}`, COL2 + 70, row);
  row += ROW_H;

  ctx.fillStyle = '#495057';
  ctx.fillText('Ranges:', COL2, row);
  ctx.fillStyle = '#6f42c1';
  const rangesDisplay = paraRanges.length > 0 ? paraRanges.join(' ') : 'none';
  ctx.fillText(rangesDisplay.length > 22 ? rangesDisplay.slice(0, 22) + '…' : rangesDisplay, COL2 + 70, row);
  row += ROW_H;

  ctx.fillStyle = '#495057';
  ctx.fillText('Per-para:', COL2, row);
  ctx.fillStyle = '#6c757d';
  const perParaDisplay = perParaInfo.length > 0 ? perParaInfo.join(' ') : 'none';
  ctx.fillText(perParaDisplay.length > 20 ? perParaDisplay.slice(0, 20) + '…' : perParaDisplay, COL2 + 70, row);
  row += ROW_H + 8;

  // Section: Snapshot
  ctx.fillStyle = '#6c757d';
  ctx.font = 'bold 10px system-ui';
  ctx.fillText('SNAPSHOT', COL2, row);
  row += ROW_H;

  ctx.font = '11px monospace';
  ctx.fillStyle = '#495057';
  ctx.fillText('Current:', COL2, row);
  ctx.fillStyle = '#6c757d';
  ctx.fillText(`${singleSnapshotCarets}c / ${currentSnapshot?.lines?.length || 0}L`, COL2 + 70, row);
  row += ROW_H;

  ctx.fillStyle = '#495057';
  ctx.fillText('Version:', COL2, row);
  ctx.fillStyle = '#0d6efd';
  ctx.font = 'bold 11px monospace';
  ctx.fillText(`v${snapshotVersion}`, COL2 + 70, row);

  // ═══════════════════════════════════════════════════════════════
  // LAST EDIT SECTION (full width, below both columns)
  // ═══════════════════════════════════════════════════════════════
  // Start below whichever column is taller
  const rightColEndRow = row;
  row = Math.max(leftColEndRow, rightColEndRow) + ROW_H + 12;

  // Draw a subtle divider line before LAST EDIT section
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(X + 10, row - 6);
  ctx.lineTo(X + W - 10, row - 6);
  ctx.stroke();

  const lastEdit = sharedInputContext?.getLastEditInfo?.();

  // Section header with background highlight
  ctx.fillStyle = 'rgba(108, 117, 125, 0.08)';
  ctx.fillRect(X + 4, row - 4, W - 8, ROW_H + 4);

  ctx.fillStyle = '#6c757d';
  ctx.font = 'bold 10px system-ui';
  ctx.fillText('LAST EDIT', COL1, row + 8);
  row += ROW_H + 8;

  ctx.font = '11px monospace';
  if (lastEdit) {
    // Row 1: Type + Position
    ctx.fillStyle = '#495057';
    ctx.fillText('Type:', COL1, row);
    const typeIcon = lastEdit.type === 'delete' ? '🗑️' : lastEdit.type === 'insert' ? '✏️' : '❓';
    ctx.fillStyle = lastEdit.type === 'delete' ? '#dc3545' : '#198754';
    ctx.font = 'bold 11px monospace';
    ctx.fillText(`${typeIcon} ${lastEdit.type.toUpperCase()}`, COL1 + 50, row);

    ctx.font = '11px monospace';
    ctx.fillStyle = '#495057';
    ctx.fillText('Position:', COL1 + 160, row);
    ctx.fillStyle = '#0d6efd';
    const detailText = lastEdit.type === 'delete'
      ? `@${lastEdit.position} (del ${lastEdit.deleteCount})`
      : `@${lastEdit.position} (+${lastEdit.insertLength})`;
    ctx.fillText(detailText, COL1 + 220, row);

    // Age on the right
    const ageMs = Date.now() - lastEdit.timestamp;
    const ageStr = ageMs < 1000 ? `${ageMs}ms` : `${(ageMs / 1000).toFixed(1)}s`;
    ctx.fillStyle = '#495057';
    ctx.fillText('Age:', COL2 + 100, row);
    ctx.fillStyle = ageMs < 1000 ? '#198754' : (ageMs < 5000 ? '#fd7e14' : '#6c757d');
    ctx.fillText(ageStr, COL2 + 135, row);
    row += ROW_H;

    // Row 2: Para delta + Deleted IDs
    ctx.fillStyle = '#495057';
    ctx.fillText('Para Δ:', COL1, row);
    const paraDelta = lastEdit.newParagraphCount - lastEdit.prevParagraphCount;
    ctx.fillStyle = paraDelta === 0 ? '#6c757d' : (paraDelta > 0 ? '#198754' : '#dc3545');
    ctx.font = 'bold 11px monospace';
    const deltaStr = paraDelta === 0 ? '0' : (paraDelta > 0 ? `+${paraDelta}` : `${paraDelta}`);
    ctx.fillText(`${deltaStr} (${lastEdit.prevParagraphCount}→${lastEdit.newParagraphCount})`, COL1 + 50, row);

    ctx.font = '11px monospace';
    ctx.fillStyle = '#495057';
    ctx.fillText('Deleted:', COL1 + 160, row);
    if (lastEdit.deletedParagraphIds.length > 0) {
      ctx.fillStyle = '#dc3545';
      ctx.font = 'bold 11px monospace';
      const idsDisplay = lastEdit.deletedParagraphIds.map(id => id.slice(0, 8)).join(', ');
      ctx.fillText(idsDisplay.length > 30 ? idsDisplay.slice(0, 30) + '…' : idsDisplay, COL1 + 220, row);
    } else {
      ctx.fillStyle = '#6c757d';
      ctx.fillText('none', COL1 + 220, row);
    }
  } else {
    ctx.fillStyle = '#6c757d';
    ctx.fillText('No edit recorded yet', COL1, row);
  }

  // ═══════════════════════════════════════════════════════════════
  // BOTTOM: Diagnostics + Text Preview
  // ═══════════════════════════════════════════════════════════════
  const bottomY = Y + H - 65;

  // Divider line
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(X + 10, bottomY - 8);
  ctx.lineTo(X + W - 10, bottomY - 8);
  ctx.stroke();

  // Diagnostics row
  ctx.font = 'bold 11px system-ui';
  if (diagnostics.length === 0) {
    ctx.fillStyle = '#198754';
    ctx.fillText('✅ All checks passed', COL1, bottomY + 5);
  } else {
    ctx.fillStyle = '#dc3545';
    ctx.fillText('⚠️ ' + diagnostics.join(' • '), COL1, bottomY + 5);
  }

  // Text preview section with background
  const previewY = bottomY + 18;
  ctx.fillStyle = 'rgba(248, 249, 250, 1)';
  ctx.fillRect(X + 8, previewY, W - 16, 38);
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
  ctx.lineWidth = 1;
  ctx.strokeRect(X + 8, previewY, W - 16, 38);

  ctx.fillStyle = '#6c757d';
  ctx.font = 'bold 10px system-ui';
  ctx.fillText('TEXT PREVIEW', X + 14, previewY + 12);

  // Format text preview with visible newlines
  ctx.font = '12px monospace';
  ctx.fillStyle = '#212529';
  const formattedText = typedText
    .replace(/\n/g, '⏎')
    .replace(/\t/g, '→')
    .replace(/ /g, '·');
  const maxPreviewLen = 55;
  const preview = formattedText.length > maxPreviewLen
    ? formattedText.slice(0, maxPreviewLen) + '…'
    : formattedText;
  ctx.fillText(preview || '(empty)', X + 14, previewY + 30);

  // Character count badge
  ctx.fillStyle = 'rgba(108, 117, 125, 0.2)';
  drawRoundedRect(ctx, X + W - 60, previewY + 4, 48, 16, 3);
  ctx.fill();
  ctx.fillStyle = '#6c757d';
  ctx.font = '9px system-ui';
  ctx.fillText(`${typedText.length} chars`, X + W - 56, previewY + 15);

  ctx.restore();

  // 🔬 DIAGNOSTIC: Log to console only when issues detected
  if (isStale || caretOutOfBounds || !cursorFound) {
    console.log('🔬 [DEBUG_DASHBOARD] Issues detected:', {
      typedText: typedText.length > 20 ? typedText.slice(0, 20) + '...' : typedText,
      typedLen: typedText.length,
      caretRange: `[${minCaretIdx}-${maxCaretIdx}]`,
      caretOutOfBounds,
      cursorPos,
      cursorFound,
      snapshotCount,
      paraRanges,
      totalCaretEntries,
      expectedCarets,
      perParaInfo
    });
  }
};
